import { qk, useCampaigns, useSettings } from '@restaurant/api-client/react';
import { formatMoney, type CampaignDto } from '@restaurant/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Megaphone, Pencil, Plus, Send, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState, PageHeader, Skeleton } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { toastError } from '@/lib/errors';
import { CampaignDialog } from './CampaignDialog';

const STATUS: Record<CampaignDto['status'], { label: string; variant: 'muted' | 'outline' | 'success' | 'destructive' | 'default' }> = {
  draft: { label: 'Draft', variant: 'muted' },
  scheduled: { label: 'Scheduled', variant: 'default' },
  sending: { label: 'Sending', variant: 'default' },
  sent: { label: 'Sent', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'outline' },
};

export function PromotionsPage() {
  const { data: campaigns, isLoading } = useCampaigns();
  const { data: settings } = useSettings();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<CampaignDto | 'new' | null>(null);
  const currency = settings?.currency ?? 'LKR';
  // How many customers each kind of campaign could reach right now.
  const reach = useQuery({
    queryKey: ['campaign-reach'],
    queryFn: async () => {
      const [promo, invite] = await Promise.all([api.campaigns.preview('promotion', { excludePromotedWithinDays: 0 }), api.campaigns.preview('invite', {})]);
      return { optedIn: promo.count, invitable: invite.count, remainingToday: promo.remainingToday, dailyLimit: promo.dailyLimit };
    },
  });

  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: qk.campaigns }), qc.invalidateQueries({ queryKey: ['campaign-reach'] })]);
  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
      await refresh();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Promotions"
        description="Send approved WhatsApp templates (daily specials, offers) to customers who agreed to receive them."
        actions={<Button onClick={() => setEditing('new')}><Plus /> New campaign</Button>}
      />
      <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
        <div className="mx-auto max-w-5xl space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Receive promotions" value={reach.data?.optedIn} hint="Opted in by replying YES/START, or recorded by staff" />
            <Stat label="Not asked yet" value={reach.data?.invitable} hint="Chatted with you; send them an opt-in invite" />
            <Stat label="Bulk messages left (24h)" value={reach.data ? `${reach.data.remainingToday} / ${reach.data.dailyLimit}` : undefined} hint="Change in Settings as Meta raises your limit" />
          </div>
          <p className="text-xs text-muted-foreground">
            Meta only allows approved <Link to="/templates" className="underline">templates</Link> for bulk messages (category Marketing for promotions) and charges per
            message. Customers can reply STOP at any time; they are never messaged again unless they reply START.
          </p>

          {isLoading ? (
            <Skeleton className="h-40" />
          ) : !campaigns?.length ? (
            <EmptyState
              icon={<Megaphone />}
              title="No campaigns yet"
              description="Start with an opt-in invite to past customers, then send your daily specials to everyone who says yes."
              action={<Button onClick={() => setEditing('new')}><Plus /> New campaign</Button>}
            />
          ) : (
            campaigns.map((c) => (
              <Card key={c.id} className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 font-semibold">
                      {c.name}
                      <Badge variant={STATUS[c.status].variant}>{STATUS[c.status].label}</Badge>
                      {c.kind === 'invite' ? <Badge variant="outline">Opt-in invite</Badge> : null}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Template {c.templateName} ({c.language})
                      {c.startedAt ? ` · sent ${format(new Date(c.startedAt), 'd MMM, HH:mm')}` : c.scheduledAt ? ` · scheduled for ${format(new Date(c.scheduledAt), 'd MMM, HH:mm')}` : ` · created ${format(new Date(c.createdAt), 'd MMM')}`}
                      {c.createdBy ? ` by ${c.createdBy}` : ''}
                    </p>
                    {c.lastError ? <p className="mt-1 text-xs text-destructive">Scheduled send failed: {c.lastError}</p> : null}
                  </div>
                  <div className="flex gap-1">
                    {c.status === 'draft' ? (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setEditing(c)}><Pencil /> Edit</Button>
                        <Button size="sm" variant="ghost" aria-label="Delete draft" onClick={() => void act(() => api.campaigns.remove(c.id), 'Draft deleted')}><Trash2 /></Button>
                      </>
                    ) : null}
                    {c.status === 'scheduled' ? (
                      <Button size="sm" variant="ghost" onClick={() => void act(() => api.campaigns.remove(c.id), 'Scheduled send cancelled')}><X /> Cancel</Button>
                    ) : null}
                  </div>
                </div>
                {c.stats.recipients ? (
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center sm:grid-cols-7">
                    <Metric label="Recipients" value={c.stats.recipients} />
                    <Metric label="Delivered" value={c.stats.delivered} of={c.stats.recipients} />
                    <Metric label="Read" value={c.stats.read} of={c.stats.recipients} />
                    <Metric label="Replied" value={c.stats.replied} of={c.stats.recipients} />
                    <Metric label="Orders (48h)" value={c.stats.orders} />
                    <Metric label="Revenue (48h)" value={formatMoney(c.stats.revenueCents, currency)} />
                    <Metric label={c.stats.failed ? 'Failed · opt-outs' : 'Opt-outs'} value={c.stats.failed ? `${c.stats.failed} · ${c.stats.optOuts}` : c.stats.optOuts} warn={c.stats.failed > 0} />
                  </div>
                ) : null}
                {c.status === 'sent' && c.stats.sent + c.stats.failed < c.stats.recipients ? (
                  <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground"><Send className="size-3" /> Still sending: {c.stats.recipients - c.stats.sent - c.stats.failed} queued (paced at {settings?.promoSendsPerMinute ?? 30}/min)</p>
                ) : null}
              </Card>
            ))
          )}
        </div>
      </div>
      {editing ? <CampaignDialog campaign={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); void refresh(); }} /> : null}
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: number | string | undefined; hint: string }) {
  return (
    <Card className="p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold tabular-nums">{value ?? '–'}</p>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </Card>
  );
}

function Metric({ label, value, of, warn }: { label: string; value: number | string; of?: number; warn?: boolean }) {
  return (
    <div className="rounded-lg bg-muted/60 px-2 py-1.5">
      <p className={`text-sm font-semibold tabular-nums ${warn ? 'text-destructive' : ''}`}>
        {value}
        {of ? <span className="text-xs font-normal text-muted-foreground"> {Math.round((Number(value) / of) * 100)}%</span> : null}
      </p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}
